import { useState } from 'react';
import useChatActions from './Chat.actions.ts';
import { useChatContext } from './Chat.hook.ts';

export type tUseContactBlock = {
  /** Whether the WAPI contact of the open chat is blocked on WhatsApp. */
  isBlocked: boolean;
  /** True while a block/unblock request is in flight. */
  loading: boolean;
  /**
   * Blocks/unblocks the contact and, on success, syncs `selectedChat.isBlocked`
   * locally so every consumer (ChatMenu button, composer banner) re-renders
   * without a refetch. Errors are toasted by the underlying action.
   */
  setContactBlocked: (blocked: boolean) => Promise<void>;
};

/**
 * Shared block-state seam for the open chat (INT-1294). Both the ChatMenu
 * toggle and the composer's blocked banner mutate the same state, so the
 * call-then-sync sequence lives here instead of being copied per consumer.
 */
export default function useContactBlock(): tUseContactBlock {
  const { blockContact, unblockContact } = useChatActions();
  const { selectedChat, setSelectedChat } = useChatContext();
  const [loading, setLoading] = useState(false);

  const isBlocked = Boolean(selectedChat?.isBlocked);

  async function setContactBlocked(blocked: boolean) {
    setLoading(true);
    try {
      const response = blocked ? await blockContact() : await unblockContact();
      if (response?.success) {
        setSelectedChat((prev) =>
          prev ? { ...prev, isBlocked: blocked } : prev,
        );
      }
    } catch (error) {
      // Error toast is already handled in the action, just log it here.
      console.error('Block operation failed:', error);
    } finally {
      setLoading(false);
    }
  }

  return { isBlocked, loading, setContactBlocked };
}
